import React from 'react';
import { useLessonListing } from '../../hooks/use-lesson-listing';
import { LoadingSpinner } from '../shared/components';
import { LessonCard } from './components/LessonCard';
import { ListingActions } from './components/ListingActions';
import { LessonType, Role } from '../../core/types';
import BgContentWrap from '../../../../components/layout/bg-content-wrap';
import { ConfirmationDialog } from '@/shared/components/dialogs/ConfirmationDialog';
import { Button } from '@/components/ui/button';
import { FaRotate } from 'react-icons/fa6';

// Pe listarile goale, cine nu poate crea continut (elev, profesor) vede anuntul, nu „Nu exista …".
const MESAJ_IN_CURAND =
  'Veți putea accesa în curând pe toate categoriile Lecții - Activități practice - Provocări';
const TIPURI_IN_CURAND: ReadonlySet<LessonType> = new Set<LessonType>([
  LessonType.HOMEWORK,
  LessonType.CLASS_EXERCISE,
  LessonType.PRACTICAL_ACTIVITY,
  LessonType.MONTHLY_CHALLENGE,
  LessonType.SCHOOL_CHALLENGE,
]);

interface LessonListingPageProps {
  lessonType: LessonType;
  userRole: Role;
}

/**
 * Unified lesson listing page for all lesson types
 *
 * Architecture:
 * - AuthenticatedLayout: Provides sidebar, nav bar, main container (overflow-hidden)
 * - BgContentWrap: Wraps content with shadcn ScrollArea for scrolling + design styling
 * - LessonListingPage: Provides page structure (actions bar + grid layout)
 *
 * Scrolling Strategy:
 * - BgContentWrap uses shadcn ScrollArea (Radix UI primitive)
 * - Provides proper scroll behavior for all pages using BgContentWrap
 * - Report pages override with custom scroll containers (ReportPageLayout)
 * - No nested scroll conflicts (AuthenticatedLayout uses overflow-hidden)
 *
 * Styling:
 * - Uses ecotic design tokens from src/main.css
 * - Lime border + white/40 overlay from BgContentWrap
 * - Responsive grid: 1-3 columns based on screen size
 *
 * Benefits:
 * - Single scroll implementation at BgContentWrap level
 * - Applies to all 11+ authenticated pages automatically
 * - Clean separation of concerns
 */
export const LessonListingPage: React.FC<LessonListingPageProps> = ({
  lessonType,
  userRole,
}) => {
  const {
    // Data
    lessons,
    config,

    // Loading states
    isLoading,
    isRefreshing,
    isMutating,
    isError,

    // Actions
    createLesson,
    editLesson,
    startCompletion,
    previewLesson,
    openDetails,
    openUpload,
    toggleVisibility,
    duplicateLesson,
    deleteLesson,
    deleteConfirmation,
    refresh,

    // Permissions
    canCreate,
    canEdit,
    canComplete,
    canToggleVisibility,
    canDelete,
  } = useLessonListing({
    lessonType,
    userRole,
    showHidden: true,
  });

  const inCurand = !canCreate && TIPURI_IN_CURAND.has(lessonType);

  // Initial loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" message={`Se încarcă ${config.pluralName.toLowerCase()}...`} />
      </div>
    );
  }

  return (
    <BgContentWrap transparent useScrollArea={false}>
      {/* Fixed Header - stays at top, no scroll */}
      <div className="flex-none">
        <ListingActions
          lessonTypeName={config.displayName}
          pluralName={config.pluralName}
          userRole={userRole}
          onRefresh={refresh}
          onCreate={canCreate ? createLesson : undefined}
          canCreate={canCreate}
          isRefreshing={isRefreshing}
        />
      </div>

      {/* Scrollable Content Area - fills remaining space with height constraint to trigger scrollbar */}
      <div className="flex-1 overflow-y-auto min-h-0 max-h-[calc(100vh-120px)] ecotic-table-scrollbar">
        {/* Instructiunile de mai jos presupun ca exista macar o lectie de aratat — pe o listare
            goala (ex. /exercitii-pentru-clasa fara continut) n-au ce explica. */}
        {lessons.length > 0 && (
          <>
        {/* Student Instructions - Show only for homework listings for students */}
        {lessonType === 'HOMEWORK' && userRole === 'ELEV' && (
          <div className="flex flex-col gap-2 p-4 text-sm text-foreground bg-card/50 rounded-lg border border-border mb-4">
            <p>
              <strong>Atenție! Doar prima dată când completezi o lecție primești puncte.</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                Dacă vezi butonul <strong>„Pornire"</strong>, înseamnă că nu ai făcut tema pentru acasă și vei fi punctat după completare.
              </li>
              <li>
                Dacă vezi butonul <strong>„Continuare"</strong>, înseamnă că ai început tema, dar nu ai terminat-o. Vei primi puncte după ce o termini.
              </li>
              <li>
                Dacă vezi butonul <strong>„Pornire (Nepunctată)"</strong>, înseamnă că ai terminat tema și ai primit deja punctele pentru prima completare.
              </li>
            </ul>
          </div>
        )}

        {/* Student Instructions - Show only for practical activity listings for students */}
        {lessonType === 'PRACTICAL_ACTIVITY' && userRole === 'ELEV' && (
          <div className="flex flex-col gap-2 p-4 text-sm text-foreground bg-card/50 rounded-lg border border-border mb-4">
            <p>
              <strong>Atenție! Doar prima dată când completezi o activitate practică primești puncte.</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                Dacă vezi butonul <strong>„Pornire"</strong>, înseamnă că nu ai făcut activitatea practică și vei fi punctat după completare.
              </li>
              <li>
                Dacă vezi butonul <strong>„Continuare"</strong>, înseamnă că ai început activitatea practică, dar nu ai terminat-o. Vei primi puncte după ce o termini.
              </li>
              <li>
                Dacă vezi butonul <strong>„Pornire (Nepunctată)"</strong>, înseamnă că ai terminat activitatea practică și ai primit deja punctele pentru prima completare.
              </li>
              <li>
                Dacă apesi pe butonul <strong>„Previzualizare"</strong>, poți studia activitatea practică fără a fi punctat pentru completare.
              </li>
            </ul>
          </div>
        )}

        {/* Student Instructions - Show only for monthly challenge listings for students */}
        {lessonType === 'MONTHLY_CHALLENGE' && userRole === 'ELEV' && (
          <div className="flex flex-col gap-2 p-4 text-sm text-foreground bg-card/50 rounded-lg border border-border mb-4">
            <p>
              <strong>Atenție! Doar prima dată când completezi o provocare primești puncte.</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                Dacă vezi butonul <strong>„Pornire"</strong>, înseamnă că nu ai completat provocarea și vei fi punctat după ce o termini.
              </li>
              <li>
                Dacă vezi butonul <strong>„Continuare"</strong>, înseamnă că ai început provocarea, dar nu ai terminat-o. Vei primi puncte după ce o termini.
              </li>
              <li>
                Dacă apare badge-ul <strong>„În Așteptare Aprobare"</strong>, profesorul îți revizuiește răspunsurile. Așteptați aprobarea înainte de a continua.
              </li>
              <li>
                Dacă apare badge-ul <strong>„Aprobat"</strong>, profesorul a acceptat răspunsurile tale și ai primit punctele. Poți continua în mod de exersare.
              </li>
              <li>
                Dacă apare badge-ul <strong>„Respins"</strong>, profesorul cere reveniri. Apasă <strong>„Reîncearcă"</strong> pentru o nouă încercare cu puncte.
              </li>
              <li>
                Dacă apare butonul <strong>„Repornire (Nepunctată)"</strong>, poți exersa fără a primi puncte suplimentare.
              </li>
            </ul>
          </div>
        )}

        {/* Professor Instructions - Show only for CLASS_EXERCISE for professors */}
        {lessonType === 'CLASS_EXERCISE' && userRole === 'PROFESOR' && (
          <div className="flex flex-col gap-2 p-4 text-sm text-foreground bg-card/50 rounded-lg border border-border mb-4">
            <p>
              <strong>Exerciții în clasă - Instrucțiuni pentru profesori:</strong>
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                Accesați exercițiul și vizionați videoclipul instructiv împreună cu elevii.
              </li>
              <li>
                Realizați activitatea propusă în clasă cu toți elevii conform instrucțiunilor.
              </li>
              <li>
                Fotografiați lucrările elevilor sau activitatea realizată (dovada completării).
              </li>
              <li>
                Încărcați fotografiile în sistem folosind butonul de încărcare imagini.
              </li>
              <li>
                Trimiteți spre aprobare - administratorul va verifica și aproba.
              </li>
              <li>
                Dacă apare badge-ul <strong>„În Așteptare Aprobare"</strong>, administratorul revizuiește materialele trimise.
              </li>
              <li>
                Dacă apare badge-ul <strong>„Aprobat"</strong>, administratorul a acceptat exercițiul completat.
              </li>
              <li>
                Dacă apare badge-ul <strong>„Respins"</strong>, verificați motivul respingerii și reîncercați.
              </li>
            </ul>
          </div>
        )}
          </>
        )}

        {/* Lesson Grid */}
        {/* Un 500/retea NU mai cade tacut pe „zero lectii" (precedent #191, p3-1/p3-4) —
            starea de eroare e distincta de lista reala goala, care ramane exact cum era. */}
        {isError ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <p className="mb-2 font-semibold text-error">
                Nu am putut încărca {config.pluralName.toLowerCase()}
              </p>
              <p className="text-error mb-3">Verificați conexiunea și încercați din nou.</p>
              <Button type="button" variant="outline" onClick={refresh}>
                <FaRotate className="mr-2" />
                <span>Încearcă din nou</span>
              </Button>
            </div>
          </div>
        ) : lessons.length === 0 ? (
          // Empty State
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <config.icon className="w-16 h-16 mx-auto text-dark/80 mb-4" />
              {inCurand ? (
                <h3 className="text-lg font-semibold text-foreground mb-2">{MESAJ_IN_CURAND}</h3>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Nu există {config.pluralName.toLowerCase()}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {canCreate
                      ? `Începe prin a crea prima ${config.displayName.toLowerCase()}`
                      : 'Nu există conținut disponibil momentan'}
                  </p>
                </>
              )}
              {canCreate && (
                <button
                  onClick={createLesson}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Adaugă {config.displayName}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {lessons.map((lesson) => (
              <LessonCard
                key={lesson.id}
                lesson={lesson}
                userRole={userRole}
                onEdit={canEdit ? editLesson : undefined}
                onComplete={canComplete ? startCompletion : undefined}
                // Provocarea pentru scoala nu trece prin player: n-are previzualizare, are „Detalii".
                onPreview={lessonType === LessonType.SCHOOL_CHALLENGE ? undefined : previewLesson}
                onDetails={openDetails}
                onUpload={openUpload}
                onDuplicate={canCreate ? duplicateLesson : undefined}
                onToggleVisibility={canToggleVisibility ? toggleVisibility : undefined}
                onDelete={canDelete ? deleteLesson : undefined}
                canEdit={canEdit}
                canComplete={canComplete}
                canToggleVisibility={canToggleVisibility}
                canDelete={canDelete}
                isLoading={isMutating}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onClose={deleteConfirmation.handleCancel}
        onConfirm={deleteConfirmation.handleConfirm}
        title="Ștergi lecția?"
        description="Sigur vrei să ștergi această lecție? Această acțiune nu poate fi anulată."
        confirmText="Șterge"
        variant="destructive"
        loading={isMutating}
      />
    </BgContentWrap>
  );
};

export default LessonListingPage;

